/**
 * PATH: packages/pricelist/src/LazyWorkbookGrid.tsx
 * FILE_ID: PKG-PRICELIST-GRID-001
 * PURPOSE: Lazy-loaded AG-Grid wrapper for one pricelist section. Keeps the
 *          ~600KB AG-Grid bundle off non-pricelist routes and translates AG
 *          cell edits into the workbook hook's editCell callback.
 * WHY: Same lazy pattern as the quote builder's scope grid; one editor engine.
 * RELATED: columns.ts (column defs), usePricelistWorkbook (editCell)
 */
import { Suspense, lazy, useCallback, useMemo, useRef } from 'react'
import type { TouchEvent as ReactTouchEvent } from 'react'
import { Loader2 } from 'lucide-react'
import { useLongPress } from '@quotech/ui/mobile'
import type { GridRow } from './columns'

export interface LazyWorkbookGridProps {
  rows: GridRow[]
  columnDefs: unknown[]
  editable: boolean
  onCellEdit: (rowId: string, field: string, value: unknown) => void
  /** When provided (and editable), renders a pinned-right delete-row action. */
  onDeleteRow?: (rowId: string) => void
  /**
   * When provided, renders a pinned-LEFT "→ add to quote" action on every row.
   * Used by the Quote Builder split-screen drawer to pick a pricelist row into
   * the quote. Independent of `editable` — you can pick from a read-only list.
   */
  onPickItem?: (row: GridRow) => void
  /**
   * Right-click on a row. AG-Grid Community has no rich context menu (Enterprise
   * only), so we forward the native `cellContextMenu` event with the row id and
   * the click coordinates; the host renders its own floating row menu. The native
   * browser menu is suppressed via preventDefaultOnContextMenu.
   */
  onRowContextMenu?: (args: { rowId: string; clientX: number; clientY: number }) => void
  /**
   * Right-click on a column header (Excel "Hide column" affordance). AG-Grid's
   * columnHeaderContextMenu event carries the column but not the mouse, so the
   * wrapper captures the pointer coordinates on the way down.
   */
  onHeaderContextMenu?: (args: { colKey: string; clientX: number; clientY: number }) => void
}

/**
 * Delete-row action cell. MUST be a React component (AG-Grid's React wrapper
 * renders the cellRenderer's return as a React child) — returning a raw DOM
 * node here previously threw React error #31 ("Objects are not valid as a React
 * child: [object HTMLButtonElement]") and crashed the whole grid. `onDeleteRow`
 * is injected via cellRendererParams.
 */
export interface DeleteCellRendererProps {
  data?: GridRow
  onDeleteRow?: (rowId: string) => void
}

export function DeleteCellRenderer({ data, onDeleteRow }: DeleteCellRendererProps) {
  return (
    <button
      type="button"
      className="qb-pl-del-btn"
      title="Delete row"
      aria-label="Delete row"
      onClick={() => {
        if (data?.id && onDeleteRow) onDeleteRow(data.id)
      }}
    >
      {'\u00d7'}
    </button>
  )
}

/**
 * Pick-into-quote action cell (Quote Builder drawer). Like DeleteCellRenderer,
 * MUST be a React component, not a raw DOM node (AG-Grid React wrapper renders
 * the return as a React child). `onPickItem` is injected via cellRendererParams.
 */
export interface PickCellRendererProps {
  data?: GridRow
  onPickItem?: (row: GridRow) => void
}

export function PickCellRenderer({ data, onPickItem }: PickCellRendererProps) {
  return (
    <button
      type="button"
      className="qb-pl-pick-btn"
      title="Add to quote"
      aria-label="Add to quote"
      onClick={() => {
        if (data && onPickItem) onPickItem(data)
      }}
    >
      {'\u2192'}
    </button>
  )
}

/**
 * Visible ⋯ twin of right-click / long-press row actions. Shown on the focused
 * or hovered row (always on phone) so touch users can discover the menu.
 */
export interface OverflowCellRendererProps {
  data?: GridRow
  onRowOverflow?: (args: { rowId: string; clientX: number; clientY: number }) => void
}

export function OverflowCellRenderer({ data, onRowOverflow }: OverflowCellRendererProps) {
  return (
    <button
      type="button"
      className="qb-pl-overflow-btn"
      title="Row actions"
      aria-label="Row actions"
      onClick={(e) => {
        if (!data?.id || !onRowOverflow) return
        const rect = (e.currentTarget as HTMLButtonElement).getBoundingClientRect()
        onRowOverflow({
          rowId: data.id,
          clientX: Math.round(rect.left + rect.width / 2),
          clientY: Math.round(rect.top + rect.height / 2),
        })
      }}
    >
      {'\u22ef'}
    </button>
  )
}

const Impl = lazy(async () => {
  const [{ AgGridReact }] = await Promise.all([
    import('ag-grid-react'),
    import('ag-grid-community'),
    import('ag-grid-community/styles/ag-grid.css'),
    import('ag-grid-community/styles/ag-theme-quartz.css'),
  ])

  function Inner({ rows, columnDefs, editable, onCellEdit, onDeleteRow, onPickItem, onRowContextMenu, onHeaderContextMenu }: LazyWorkbookGridProps) {
    // Last right-click viewport position, captured before AG-Grid handles the
    // event (columnHeaderContextMenu itself has no MouseEvent).
    const lastContextClick = useRef({ x: 0, y: 0 })
    // Touch long-press target (AG-Grid has no per-cell React bind surface).
    const longPressTarget = useRef<{
      kind: 'row' | 'header'
      id: string
      x: number
      y: number
    } | null>(null)
    const { bind: bindLongPress } = useLongPress(() => {
      const t = longPressTarget.current
      if (!t) return
      if (t.kind === 'row' && onRowContextMenu) {
        onRowContextMenu({ rowId: t.id, clientX: t.x, clientY: t.y })
      } else if (t.kind === 'header' && onHeaderContextMenu) {
        onHeaderContextMenu({ colKey: t.id, clientX: t.x, clientY: t.y })
      }
    }, { enableContextMenu: false })
    // Dummy payload — real target lives in longPressTarget (resolved on touchstart).
    const longPress = bindLongPress(null as never)
    const defaultColDef = useMemo(
      () => ({ sortable: true, resizable: true, filter: false, suppressMovable: false }),
      [],
    )
    const handleCellValueChanged = useCallback(
      (evt: { column: { getColId(): string }; data?: GridRow; newValue: unknown }) => {
        const colId = evt.column.getColId()
        if (!colId || !evt.data?.id) return
        onCellEdit(evt.data.id, colId, evt.newValue)
      },
      [onCellEdit],
    )
    const handleCellContextMenu = useCallback(
      (evt: { event?: Event; data?: GridRow }) => {
        if (!onRowContextMenu || !evt.data?.id) return
        const mouse = evt.event as MouseEvent | undefined
        if (mouse) mouse.preventDefault()
        onRowContextMenu({
          rowId: evt.data.id,
          clientX: mouse?.clientX ?? 0,
          clientY: mouse?.clientY ?? 0,
        })
      },
      [onRowContextMenu],
    )
    const handleHeaderContextMenu = useCallback(
      (evt: { column?: { getColId?: () => string } | null }) => {
        const colKey = evt.column?.getColId?.()
        if (!onHeaderContextMenu || !colKey) return
        onHeaderContextMenu({
          colKey,
          clientX: lastContextClick.current.x,
          clientY: lastContextClick.current.y,
        })
      },
      [onHeaderContextMenu],
    )
    // Append a pinned delete-row action column rendered by a React component
    // (DeleteCellRenderer). onDeleteRow is passed via cellRendererParams.
    // Deletion is staged in the hook (deletedRef) and persisted on the next
    // Save via bulk_upsert delete_ids — nothing is destroyed until the user saves.
    const colsWithActions = useMemo(() => {
      let cols = columnDefs as unknown[]
      // Pinned-LEFT pick action (drawer "→ add to quote"), independent of editable.
      if (onPickItem) {
        cols = [
          {
            headerName: '',
            colId: '__pick__',
            width: 46,
            pinned: 'left',
            editable: false,
            sortable: false,
            filter: false,
            resizable: false,
            suppressMovable: true,
            cellRenderer: PickCellRenderer,
            cellRendererParams: { onPickItem },
          },
          ...cols,
        ]
      }
      // Pinned-RIGHT overflow (⋯) — discoverable twin of long-press / right-click.
      if (onRowContextMenu) {
        cols = [
          ...cols,
          {
            headerName: '',
            colId: '__overflow__',
            width: 44,
            pinned: 'right',
            editable: false,
            sortable: false,
            filter: false,
            resizable: false,
            suppressMovable: true,
            cellRenderer: OverflowCellRenderer,
            cellRendererParams: {
              onRowOverflow: (args: { rowId: string; clientX: number; clientY: number }) =>
                onRowContextMenu(args),
            },
          },
        ]
      }
      // Pinned-RIGHT delete action (only when the section is editable).
      if (editable && onDeleteRow) {
        cols = [
          ...cols,
          {
            headerName: '',
            colId: '__actions__',
            width: 52,
            pinned: 'right',
            editable: false,
            sortable: false,
            filter: false,
            resizable: false,
            suppressMovable: true,
            cellRenderer: DeleteCellRenderer,
            cellRendererParams: { onDeleteRow },
          },
        ]
      }
      return cols
    }, [columnDefs, editable, onDeleteRow, onPickItem, onRowContextMenu])
    // Give AG Grid a resolved viewport so it owns both scroll axes. With
    // autoHeight the page became the vertical scroller, leaving the horizontal
    // scrollbar reachable only after scrolling to the bottom of long sections.
    const onTouchStartCapture = (e: ReactTouchEvent) => {
      const touch = e.touches[0]
      if (!touch) return
      const el = e.target as Element | null
      if (!el?.closest) return
      const header = el.closest('.ag-header-cell') as HTMLElement | null
      if (header && onHeaderContextMenu) {
        const colKey = header.getAttribute('col-id')
        if (colKey && !colKey.startsWith('__')) {
          longPressTarget.current = {
            kind: 'header',
            id: colKey,
            x: touch.clientX,
            y: touch.clientY,
          }
          longPress.onTouchStart(e)
          return
        }
      }
      if (!onRowContextMenu) return
      if (el.closest('.qb-pl-overflow-btn, .qb-pl-del-btn, .qb-pl-pick-btn')) return
      const row = el.closest('.ag-row') as HTMLElement | null
      const rowId = row?.getAttribute('row-id')
      if (!rowId) return
      longPressTarget.current = {
        kind: 'row',
        id: rowId,
        x: touch.clientX,
        y: touch.clientY,
      }
      longPress.onTouchStart(e)
    }
    return (
      <div
        className="ag-theme-quartz"
        style={{ width: '100%', height: 'min(72vh, 720px)', minHeight: 420 }}
        onContextMenuCapture={(e) => {
          lastContextClick.current = { x: e.clientX, y: e.clientY }
        }}
        onTouchStartCapture={onTouchStartCapture}
        onTouchMove={longPress.onTouchMove}
        onTouchEnd={longPress.onTouchEnd}
        onTouchCancel={longPress.onTouchCancel}
      >
        <AgGridReact
          rowData={rows as never}
          columnDefs={colsWithActions as never}
          defaultColDef={defaultColDef}
          onCellValueChanged={handleCellValueChanged as never}
          onCellContextMenu={handleCellContextMenu as never}
          onColumnHeaderContextMenu={onHeaderContextMenu ? (handleHeaderContextMenu as never) : undefined}
          preventDefaultOnContextMenu={Boolean(onRowContextMenu || onHeaderContextMenu)}
          animateRows={false}
          stopEditingWhenCellsLoseFocus={true}
          getRowId={(p: { data: GridRow }) => p.data.id}
          rowClassRules={{
            'qb-pl-draft': (p: { data?: GridRow }) => p.data?.is_active === false,
            'qb-pl-new': (p: { data?: GridRow }) => Boolean(p.data?.__new),
          }}
          suppressClickEdit={!editable}
          rowHeight={30}
          headerHeight={34}
          enableBrowserTooltips={true}
        />
      </div>
    )
  }
  return { default: Inner }
})

export default function LazyWorkbookGrid(props: LazyWorkbookGridProps) {
  return (
    <Suspense
      fallback={
        <div className="flex h-full min-h-[200px] items-center justify-center gap-2 text-xs text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading grid…
        </div>
      }
    >
      <Impl {...props} />
    </Suspense>
  )
}
